import { useAppContextState } from 'modules/common/contexts/AppContext'
import { useUserLocale } from 'modules/common/hooks/useUserLocale'
import { trackEvent } from 'modules/common/lib/amplitude/trackEvent'
import { pushGTMProperties } from 'modules/common/lib/gtm/dataLayer'
import { USER_COUNTRY_MAP } from 'modules/common/types/locale'
import { useCallback } from 'react'
import { SignupFormData } from '../hooks/useSignupForm'
import { getAccessToken, getAuthenticatedUser } from '../lib/auth0/token'
import { EnumAuthTrackingEvents } from '../lib/tracking/events'
import { usePostLoginUserMutation } from '../mutations/usePostLoginUserMutation'
import { usePostUserMutation } from '../mutations/usePostUserMutation'
import { useSignupStoreData, useSignupStoreType } from '../store/useSignupStore'

export const useSignupFlow = () => {
  const { landingPageParameters } = useAppContextState()
  const signupType = useSignupStoreType()
  const { publicSponsorshipVoucher } = useSignupStoreData()

  const userLocale = useUserLocale()

  const { mutateAsync: createUser } = usePostUserMutation({
    mutationOptions: {
      onMutate: () =>
        trackEvent({
          eventType: EnumAuthTrackingEvents.SIGNUP,
        }),
      onSuccess: (data) => {
        // Necessary for the SIGNUP_SUCCESS event to have access to user's email in GTM
        pushGTMProperties({
          email: data?.email,
        })
        trackEvent({
          eventType: EnumAuthTrackingEvents.SIGNUP_SUCCESS,
        })
      },
      onError: (error) => {
        if (error?.response?.data.statusCode === 409) {
          trackEvent({
            eventType:
              EnumAuthTrackingEvents.SIGNUP_FAILED_ACCOUNT_ALREADY_EXISTS,
          })
        } else {
          trackEvent({
            eventType: EnumAuthTrackingEvents.SIGNUP_FAILED_TECHNICAL_ERROR,
          })
        }
      },
    },
  })

  const { mutateAsync: loginUser } = usePostLoginUserMutation()

  const signup = useCallback(
    async (data: SignupFormData) => {
      const {
        email,
        password,
        firstname,
        lastname,
        userAcceptsCommercialSolicitation,
        captchaToken,
        userAcceptsTerms,
        hasConsentedForMarketing,
        hasConsentedForThirdPartyCommunication,
      } = data

      if (!email || !password || !firstname || !lastname || !captchaToken) {
        throw new Error()
      }

      await createUser({
        email,
        firstname,
        lastname,
        password,
        signUpInformation: {
          platform: 'WEB',
          ...landingPageParameters,
        },
        userAcceptsCommercialSolicitation,
        hasAcknowledgedPrivacyPolicy: userAcceptsTerms,
        hasConsentedForMarketing,
        hasConsentedForThirdPartyCommunication,
        sponsorshipVoucherCode: publicSponsorshipVoucher?.voucherCode,
        signupType,
        country: USER_COUNTRY_MAP[userLocale],
        captchaToken,
      })

      await loginUser({
        email,
        password,
      })
      const accessToken = getAccessToken()

      if (!accessToken) {
        throw new Error('useSignupFlow: Access token is missing after login')
      }
      // We need to have corporateId and auth0Id available in GTM when CREATE_CORPORATE_SUCCESS is sent
      const user = getAuthenticatedUser(accessToken)

      pushGTMProperties({
        corporateId: user.corporateId,
        auth0Id: user.sub,
      })

      // CREATE_CORPORATE_SUCCESS is only kept to maintain compatibility with GTM tracking
      trackEvent({
        eventType: EnumAuthTrackingEvents.CREATE_CORPORATE_SUCCESS,
      })
    },
    [
      createUser,
      landingPageParameters,
      publicSponsorshipVoucher,
      loginUser,
      signupType,
      userLocale,
    ]
  )

  return { signup }
}
